import { useEffect, useState } from "react";
import axios from "axios";
import {
    FaUser,
    FaHotel,
    FaCalendarAlt,
    FaEnvelope,
    FaPhone,
    FaGlobe,
    FaCheckCircle,
    FaTimesCircle,
    FaStar,
    FaEdit,
    FaTrash,
    FaArrowRight,
    FaClock,
    FaLock,
} from "react-icons/fa";

const COLORS = {
    orange: "#FF5A1F",
    yellow: "#F7B928",
    blue: "#0B6E8E",
    navy: "#002B45",
    background: "#F7F5EF",
    lightBlue: "#F4F8FC",
    white: "#FFFFFF",
    muted: "#71808A",
    border: "#DCE7ED",
};

const inputClass =
    "w-full rounded-xl border border-[#DCE7ED] bg-[#F4F8FC] py-3.5 pl-11 pr-4 text-sm text-[#002B45] outline-none transition duration-300 placeholder:text-[#71808A] focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/10";

const primaryButton =
    "group inline-flex items-center justify-center gap-3 rounded-full bg-[#FF5A1F] px-6 py-3.5 text-sm font-bold text-white shadow-sm transition duration-300 hover:-translate-y-0.5 hover:bg-[#E94B15] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60";

function SectionEyebrow({ children, light = false }) {
    return (
        <div className="flex items-center gap-3">
            <span
                className={`h-[2px] w-8 rounded-full ${light ? "bg-[#F7B928]" : "bg-[#FF5A1F]"
                    }`}
            />
            <span
                className={`text-[10px] font-extrabold uppercase tracking-[0.22em] ${light ? "text-[#F7B928]" : "text-[#0B6E8E]"
                    }`}
            >
                {children}
            </span>
        </div>
    );
}

function IconCircle({
    children,
    color = "blue",
    size = "h-11 w-11",
}) {
    const styles = {
        blue: "bg-[#0B6E8E] text-white",
        orange: "bg-[#FF5A1F] text-white",
        yellow: "bg-[#F7B928] text-[#002B45]",
        pale: "bg-[#E7F2F7] text-[#0B6E8E]",
        navy: "bg-[#002B45] text-[#F7B928]",
    };

    return (
        <div
            className={`flex ${size} shrink-0 items-center justify-center rounded-2xl ${styles[color]}`}
        >
            {children}
        </div>
    );
}

function InfoCard({ icon, label, children, highlight = false }) {
    return (
        <div
            className={`rounded-2xl border p-4 transition duration-300 hover:-translate-y-0.5 hover:shadow-md ${highlight
                ? "border-[#F7B928]/50 bg-[#FFF9E9]"
                : "border-[#DCE7ED] bg-[#F4F8FC]"
                }`}
        >
            <div className="mb-4">
                <IconCircle
                    color={highlight ? "yellow" : "blue"}
                    size="h-9 w-9"
                >
                    <span className="text-xs">{icon}</span>
                </IconCircle>
            </div>

            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#71808A]">
                {label}
            </p>

            <div className="mt-1.5 break-words text-sm font-bold text-[#002B45]">
                {children}
            </div>
        </div>
    );
}

function Field({
    label,
    name,
    value,
    onChange,
    icon,
    type = "text",
    placeholder,
}) {
    return (
        <div>
            <label
                htmlFor={`profile-${name}`}
                className="mb-2 block text-xs font-bold text-[#002B45]"
            >
                {label}
            </label>

            <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-[#0B6E8E]">
                    {icon}
                </span>

                <input
                    id={`profile-${name}`}
                    type={type}
                    name={name}
                    value={value || ""}
                    onChange={onChange}
                    placeholder={placeholder}
                    className={inputClass}
                />
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const normalized = String(status || "pending").toLowerCase();

    const styles =
        normalized === "confirmed"
            ? "bg-[#E5F5EC] text-[#167448]"
            : normalized === "cancelled"
                ? "bg-[#FFF0EC] text-[#C73D23]"
                : "bg-[#FFF7DE] text-[#8A6410]";

    return (
        <span
            className={`inline-flex w-fit items-center gap-2 rounded-full px-3.5 py-2 text-[10px] font-extrabold uppercase tracking-wider ${styles}`}
        >
            {normalized === "confirmed" ? (
                <FaCheckCircle />
            ) : normalized === "cancelled" ? (
                <FaTimesCircle />
            ) : (
                <FaClock />
            )}

            {normalized}
        </span>
    );
}

export default function Profile({ onLoginClick }) {
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({});
    const [bookings, setBookings] = useState([]);
    const [activeTab, setActiveTab] = useState("profile");
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(new Date());
    const [cancelLoading, setCancelLoading] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);

    // Keep cancellation countdowns updated.
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Load the logged-in user.
    useEffect(() => {
        try {
            const storedUser = JSON.parse(
                localStorage.getItem("user")
            );

            if (!storedUser) {
                setUser(null);
                setBookings([]);
                return;
            }

            setUser(storedUser);

            setForm({
                name: storedUser.name || "",
                email: storedUser.email || "",
                phone: storedUser.phone || "",
                nationality: storedUser.nationality || "",
            });

            fetchBookings(storedUser.id);
        } catch (error) {
            console.error("Unable to load user:", error);
            setUser(null);
        }
    }, []);

    // Fetch the user's bookings.
    const fetchBookings = async (userId) => {
        try {
            const res = await axios.get(
                `https://cheapdubaihotels.com/api/user/${userId}/bookings`
            );

            setBookings(
                Array.isArray(res.data) ? res.data : []
            );
        } catch (error) {
            console.error("Unable to fetch bookings:", error);
        }
    };

    // Profile form.
    const handleChange = (e) => {
        setForm((previous) => ({
            ...previous,
            [e.target.name]: e.target.value,
        }));
    };

    const updateProfile = async (e) => {
        e.preventDefault();

        if (!user?.id) return;

        setProfileLoading(true);

        try {
            const res = await axios.put(
                `https://cheapdubaihotels.com/api/user/${user.id}`,
                form
            );

            const updatedUser = res.data.user;

            localStorage.setItem(
                "user",
                JSON.stringify(updatedUser)
            );

            setUser(updatedUser);

            setForm({
                name: updatedUser.name || "",
                email: updatedUser.email || "",
                phone: updatedUser.phone || "",
                nationality: updatedUser.nationality || "",
            });

            alert("Profile updated successfully");
        } catch (error) {
            console.error(error);
            alert(
                error.response?.data?.message ||
                "Update failed"
            );
        } finally {
            setProfileLoading(false);
        }
    };

    // Date helpers.
    const formatDate = (date) => {
        if (!date) return "—";

        const parsed = new Date(date);

        if (Number.isNaN(parsed.getTime())) return "—";

        return parsed.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const normalizeDate = (date) => {
        const parsed = new Date(date);

        if (Number.isNaN(parsed.getTime())) {
            return new Date(0);
        }

        parsed.setHours(0, 0, 0, 0);

        return parsed;
    };

    const isPastCheckout = (date) =>
        normalizeDate(date) < normalizeDate(now);

    // Cancellation deadline: 24 hours after booking,
    // or check-in, whichever comes first.
    const getCancelDeadline = (booking) => {
        const createdAt = new Date(booking.created_at);
        const checkIn = new Date(booking.check_in);

        if (
            Number.isNaN(createdAt.getTime()) ||
            Number.isNaN(checkIn.getTime())
        ) {
            return new Date(0);
        }

        const freeCancelUntil = new Date(
            createdAt.getTime() + 24 * 60 * 60 * 1000
        );

        return freeCancelUntil < checkIn
            ? freeCancelUntil
            : checkIn;
    };

    const canCancel = (booking) =>
        now < getCancelDeadline(booking);

    const getCancelCountdown = (booking) => {
        const diff = getCancelDeadline(booking) - now;

        if (diff <= 0) return null;

        const totalSeconds = Math.floor(diff / 1000);

        return {
            hours: Math.floor(totalSeconds / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60,
        };
    };

    // Cancel a booking.
    const cancelBooking = async (id) => {
        if (
            !window.confirm(
                "Are you sure you want to cancel this booking?"
            )
        ) {
            return;
        }

        try {
            setCancelLoading(id);

            await axios.post(
                `https://cheapdubaihotels.com/api/booking/${id}/cancel`
            );

            alert("Booking cancelled");

            await fetchBookings(user.id);
        } catch (error) {
            alert(
                error.response?.data?.message ||
                "Cancel failed"
            );
        } finally {
            setCancelLoading(null);
        }
    };

    // Review modal.
    const openReviewModal = (booking) => {
        setSelectedBooking(booking);
        setRating(0);
        setComment("");
        setShowReviewModal(true);
    };

    const handleEditReview = (booking) => {
        setSelectedBooking(booking);
        setRating(booking.review?.rating || 0);
        setComment(booking.review?.comment || "");
        setShowReviewModal(true);
    };

    const submitReview = async () => {
        if (!rating) {
            alert("Please select a rating");
            return;
        }

        if (!selectedBooking || !user) return;

        setLoading(true);

        try {
            if (selectedBooking.review) {
                await axios.put(
                    `https://cheapdubaihotels.com/api/reviews/${selectedBooking.review.id}`,
                    {
                        rating,
                        comment,
                    }
                );

                alert("Review updated!");
            } else {
                await axios.post(
                    "https://cheapdubaihotels.com/api/reviews",
                    {
                        booking_id: selectedBooking.id,
                        hotel_id: selectedBooking.hotel_id,
                        email: user.email,
                        rating,
                        comment,
                    }
                );

                alert("Review submitted!");
            }

            setShowReviewModal(false);
            setSelectedBooking(null);
            setRating(0);
            setComment("");

            await fetchBookings(user.id);
        } catch (error) {
            alert(
                error.response?.data?.message || "Error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteReview = async (reviewId) => {
        if (!window.confirm("Delete this review?")) return;

        try {
            await axios.delete(
                `https://cheapdubaihotels.com/api/reviews/${reviewId}`
            );

            alert("Review deleted");

            await fetchBookings(user.id);
        } catch (error) {
            alert(
                error.response?.data?.message ||
                "Delete failed"
            );
        }
    };

    const closeReviewModal = () => {
        if (loading) return;

        setShowReviewModal(false);
        setSelectedBooking(null);
    };

    // Login state.
    if (!user) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F7F5EF] px-5 py-20">
                <div className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-[#DCE7ED] bg-white shadow-[0_25px_80px_rgba(0,43,69,0.12)]">
                    <div className="flex h-1.5">
                        <div className="w-1/2 bg-[#FF5A1F]" />
                        <div className="w-1/2 bg-[#F7B928]" />
                    </div>

                    <div className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[#0B6E8E]/10 blur-3xl" />

                    <div className="relative p-8 text-center sm:p-10">
                        <IconCircle
                            color="blue"
                            size="h-16 w-16 mx-auto rounded-2xl"
                        >
                            <FaLock className="text-xl" />
                        </IconCircle>

                        <div className="mt-7 flex justify-center">
                            <SectionEyebrow>
                                My Account
                            </SectionEyebrow>
                        </div>

                        <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-[#002B45] sm:text-4xl">
                            Welcome Back
                        </h1>

                        <p className="mx-auto mt-4 max-w-sm text-sm leading-7 text-[#71808A]">
                            Sign in to access your profile,
                            reservations, cancellations, and
                            hotel reviews.
                        </p>

                        <button
                            onClick={onLoginClick}
                            className={`${primaryButton} mt-8 w-full`}
                        >
                            Go to Login

                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F7B928] text-[#002B45] transition group-hover:translate-x-1">
                                <FaArrowRight className="text-xs" />
                            </span>
                        </button>

                        <p className="mt-6 text-xs text-[#71808A]">
                            Your stay, your account, all in one place.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen overflow-hidden bg-[#F7F5EF] mt-28">
            {/* ACCOUNT HERO */}

            <section className="relative overflow-hidden bg-[#002B45]">
                <div className="pointer-events-none absolute -right-32 -top-36 h-[430px] w-[430px] rounded-full bg-[#0B6E8E]/35 blur-3xl" />

                <div className="pointer-events-none absolute -bottom-40 -left-20 h-[380px] w-[380px] rounded-full bg-[#FF5A1F]/15 blur-3xl" />

                <div className="pointer-events-none absolute right-[10%] top-1/2 hidden h-64 w-64 -translate-y-1/2 rounded-full border border-white/10 lg:block" />

                <div className="pointer-events-none absolute right-[13%] top-1/2 hidden h-40 w-40 -translate-y-1/2 rounded-full border border-[#F7B928]/30 lg:block" />

                <div className="relative mx-auto max-w-[1536px] px-5 py-10 sm:px-8 sm:py-12 md:py-14 lg:px-10">
                    <div className="max-w-3xl">
                        <SectionEyebrow light>
                            My Account
                        </SectionEyebrow>

                        <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-[60px]">
                            Welcome,

                            <span className="mt-1 block break-words text-[#F7B928]">
                                {user.name}
                            </span>
                        </h1>

                        <p className="mt-5 max-w-2xl text-sm leading-7 text-white/70 sm:text-base">
                            Manage your personal information,
                            hotel reservations, cancellations,
                            and reviews — all from one place.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <div className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.07] px-4 py-3 backdrop-blur-sm">
                                <FaCheckCircle className="text-[#F7B928]" />

                                <span className="text-xs font-bold text-white">
                                    Account Active
                                </span>
                            </div>

                            <div className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.07] px-4 py-3 backdrop-blur-sm">
                                <FaHotel className="text-[#F7B928]" />

                                <span className="text-xs font-bold text-white">
                                    {bookings.length}{" "}
                                    {bookings.length === 1
                                        ? "Booking"
                                        : "Bookings"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 flex h-1 w-full">
                    <div className="w-2/3 bg-[#FF5A1F]" />
                    <div className="w-1/3 bg-[#F7B928]" />
                </div>
            </section>

            {/* ACCOUNT CONTENT */}

            <section className="relative py-12 sm:py-14 lg:py-16">
                <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-[#0B6E8E]/5 blur-3xl" />

                <div className="relative z-10 mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">
                    {/* Section heading */}

                    <div className="mb-9 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
                        <div>
                            <SectionEyebrow>
                                Account Dashboard
                            </SectionEyebrow>

                            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-[#002B45] sm:text-4xl">
                                Manage Your Account
                            </h2>

                            <p className="mt-3 max-w-xl text-sm leading-7 text-[#71808A]">
                                Update your details and manage your
                                hotel reservations from your personal
                                dashboard.
                            </p>
                        </div>

                        <div className="flex w-fit items-center gap-4 rounded-2xl border border-[#DCE7ED] bg-white px-5 py-4 shadow-[0_10px_35px_rgba(0,43,69,0.05)]">
                            <IconCircle color="orange">
                                <FaHotel />
                            </IconCircle>

                            <div>
                                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#71808A]">
                                    Total Bookings
                                </p>

                                <p className="mt-1 text-2xl font-extrabold text-[#002B45]">
                                    {bookings.length}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Dashboard layout */}

                    <div className="grid items-start gap-7 lg:grid-cols-[270px_minmax(0,1fr)]">
                        {/* SIDEBAR */}

                        <aside>
                            <div className="rounded-[24px] border border-[#DCE7ED] bg-white p-5 shadow-[0_12px_40px_rgba(0,43,69,0.05)] lg:sticky lg:top-24">
                                <div className="mb-6 rounded-[18px] border border-[#DCE7ED] bg-[#F4F8FC] p-4">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <IconCircle
                                            color="blue"
                                            size="h-12 w-12 rounded-full"
                                        >
                                            <FaUser />
                                        </IconCircle>

                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-extrabold text-[#002B45]">
                                                {user.name}
                                            </p>

                                            <p className="mt-1 truncate text-xs text-[#71808A]">
                                                {user.email}
                                            </p>

                                            <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold text-[#167448]">
                                                <FaCheckCircle />
                                                Active account
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <SectionEyebrow>
                                    Navigation
                                </SectionEyebrow>

                                <h3 className="mb-5 mt-3 text-xl font-extrabold text-[#002B45]">
                                    My Account
                                </h3>

                                <div className="space-y-2">
                                    <button
                                        onClick={() =>
                                            setActiveTab("profile")
                                        }
                                        className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-bold transition duration-200 ${activeTab === "profile"
                                            ? "bg-[#0B6E8E] text-white shadow-[0_8px_20px_rgba(11,110,142,0.18)]"
                                            : "text-[#71808A] hover:bg-[#F4F8FC] hover:text-[#002B45]"
                                            }`}
                                    >
                                        <span
                                            className={`flex h-10 w-10 items-center justify-center rounded-xl transition ${activeTab === "profile"
                                                ? "bg-white/15 text-[#F7B928]"
                                                : "bg-[#E7F2F7] text-[#0B6E8E]"
                                                }`}
                                        >
                                            <FaUser />
                                        </span>

                                        <span className="flex-1">
                                            Profile
                                        </span>

                                        <FaArrowRight
                                            className={`text-[10px] transition ${activeTab === "profile"
                                                ? "translate-x-0 opacity-100"
                                                : "-translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                                                }`}
                                        />
                                    </button>

                                    <button
                                        onClick={() =>
                                            setActiveTab("bookings")
                                        }
                                        className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-bold transition duration-200 ${activeTab === "bookings"
                                            ? "bg-[#0B6E8E] text-white shadow-[0_8px_20px_rgba(11,110,142,0.18)]"
                                            : "text-[#71808A] hover:bg-[#F4F8FC] hover:text-[#002B45]"
                                            }`}
                                    >
                                        <span
                                            className={`flex h-10 w-10 items-center justify-center rounded-xl ${activeTab === "bookings"
                                                ? "bg-white/15 text-[#F7B928]"
                                                : "bg-[#E7F2F7] text-[#0B6E8E]"
                                                }`}
                                        >
                                            <FaHotel />
                                        </span>

                                        <span className="flex-1">
                                            My Bookings
                                        </span>

                                        <span
                                            className={`min-w-7 rounded-full px-2 py-1 text-center text-[10px] font-extrabold ${activeTab === "bookings"
                                                ? "bg-[#F7B928] text-[#002B45]"
                                                : "bg-[#E7F2F7] text-[#0B6E8E]"
                                                }`}
                                        >
                                            {bookings.length}
                                        </span>
                                    </button>
                                </div>

                                <div className="mt-6 rounded-xl border border-[#F7B928]/40 bg-[#FFF9E9] p-4">
                                    <div className="flex items-center gap-2 text-[#002B45]">
                                        <FaLock className="text-[#0B6E8E]" />

                                        <p className="text-xs font-extrabold">
                                            Your account
                                        </p>
                                    </div>

                                    <p className="mt-2 text-xs leading-5 text-[#71808A]">
                                        Keep your contact details up to
                                        date for a smoother booking
                                        experience.
                                    </p>
                                </div>
                            </div>
                        </aside>

                        {/* MAIN CONTENT */}

                        <main className="min-w-0">
                            {/* PROFILE TAB */}

                            {activeTab === "profile" && (
                                <div className="overflow-hidden rounded-[24px] border border-[#DCE7ED] bg-white shadow-[0_12px_45px_rgba(0,43,69,0.05)]">
                                    <div className="flex h-1.5">
                                        <div className="w-1/2 bg-[#FF5A1F]" />
                                        <div className="w-1/2 bg-[#F7B928]" />
                                    </div>

                                    <div className="p-5 sm:p-8 lg:p-10">
                                        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center gap-4">
                                                <IconCircle color="pale">
                                                    <FaUser />
                                                </IconCircle>

                                                <div>
                                                    <h2 className="text-2xl font-extrabold tracking-tight text-[#002B45] sm:text-3xl">
                                                        Profile Information
                                                    </h2>

                                                    <p className="mt-1 text-xs leading-5 text-[#71808A]">
                                                        Keep your account
                                                        information up to date.
                                                    </p>
                                                </div>
                                            </div>

                                            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[#E5F5EC] px-3 py-2 text-[10px] font-extrabold text-[#167448]">
                                                <FaCheckCircle />
                                                Account Active
                                            </span>
                                        </div>

                                        <form
                                            onSubmit={updateProfile}
                                            className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                                        >
                                            <Field
                                                label="Full Name"
                                                name="name"
                                                value={form.name}
                                                onChange={handleChange}
                                                icon={<FaUser />}
                                                placeholder="Full Name"
                                            />

                                            <Field
                                                label="Email Address"
                                                name="email"
                                                type="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                icon={<FaEnvelope />}
                                                placeholder="Email Address"
                                            />

                                            <Field
                                                label="Phone Number"
                                                name="phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={handleChange}
                                                icon={<FaPhone />}
                                                placeholder="Phone Number"
                                            />

                                            <Field
                                                label="Nationality"
                                                name="nationality"
                                                value={form.nationality}
                                                onChange={handleChange}
                                                icon={<FaGlobe />}
                                                placeholder="Nationality"
                                            />

                                            <div className="pt-3 sm:col-span-2">
                                                <button
                                                    type="submit"
                                                    disabled={profileLoading}
                                                    className={primaryButton}
                                                >
                                                    {profileLoading
                                                        ? "Updating..."
                                                        : "Update Profile"}

                                                    {!profileLoading && (
                                                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F7B928] text-[#002B45] transition group-hover:translate-x-1">
                                                            <FaArrowRight className="text-xs" />
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* BOOKINGS TAB */}

                            {activeTab === "bookings" && (
                                <div className="overflow-hidden rounded-[24px] border border-[#DCE7ED] bg-white shadow-[0_12px_45px_rgba(0,43,69,0.05)]">
                                    <div className="flex h-1.5">
                                        <div className="w-1/2 bg-[#FF5A1F]" />
                                        <div className="w-1/2 bg-[#F7B928]" />
                                    </div>

                                    <div className="p-5 sm:p-8 lg:p-10">
                                        <div className="mb-8 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                                            <div className="flex items-center gap-4">
                                                <IconCircle color="pale">
                                                    <FaHotel />
                                                </IconCircle>

                                                <div>
                                                    <h2 className="text-2xl font-extrabold tracking-tight text-[#002B45] sm:text-3xl">
                                                        My Bookings
                                                    </h2>

                                                    <p className="mt-1 text-xs leading-6 text-[#71808A] sm:text-sm">
                                                        Manage reservations,
                                                        cancellations, and
                                                        reviews.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex w-fit items-center gap-3 rounded-2xl border border-[#DCE7ED] bg-[#F4F8FC] px-4 py-3">
                                                <IconCircle
                                                    color="orange"
                                                    size="h-10 w-10"
                                                >
                                                    <FaCalendarAlt />
                                                </IconCircle>

                                                <div>
                                                    <p className="text-[9px] font-extrabold uppercase tracking-wider text-[#71808A]">
                                                        Total Bookings
                                                    </p>

                                                    <p className="text-xl font-extrabold text-[#002B45]">
                                                        {bookings.length}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Empty state */}

                                        {bookings.length === 0 ? (
                                            <div className="rounded-[22px] border border-dashed border-[#DCE7ED] bg-[#F4F8FC] px-5 py-16 text-center sm:py-20">
                                                <IconCircle
                                                    color="blue"
                                                    size="h-16 w-16 mx-auto rounded-2xl"
                                                >
                                                    <FaHotel className="text-xl" />
                                                </IconCircle>

                                                <div className="mt-6 flex justify-center">
                                                    <SectionEyebrow>
                                                        Reservations
                                                    </SectionEyebrow>
                                                </div>

                                                <h3 className="mt-4 text-2xl font-extrabold text-[#002B45]">
                                                    No bookings yet
                                                </h3>

                                                <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-[#71808A]">
                                                    Your reservations will
                                                    appear here once you make
                                                    a booking.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {bookings.map((b) => (
                                                    <article
                                                        key={b.id}
                                                        className="overflow-hidden rounded-[22px] border border-[#DCE7ED] bg-white transition duration-300 hover:-translate-y-0.5 hover:border-[#0B6E8E]/30 hover:shadow-[0_18px_45px_rgba(0,43,69,0.08)]"
                                                    >
                                                        {/* Booking header */}

                                                        <div className="border-b border-[#DCE7ED] bg-[#F4F8FC] p-5 sm:p-6">
                                                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                                                <div className="flex min-w-0 items-start gap-4">
                                                                    <IconCircle
                                                                        color="blue"
                                                                        size="h-12 w-12 rounded-2xl"
                                                                    >
                                                                        <FaHotel />
                                                                    </IconCircle>

                                                                    <div className="min-w-0">
                                                                        <p className="mb-1 text-[9px] font-extrabold uppercase tracking-[0.18em] text-[#FF5A1F]">
                                                                            Reservation
                                                                        </p>

                                                                        <h3 className="break-words text-lg font-extrabold text-[#002B45] sm:text-xl">
                                                                            {b.hotel_name ||
                                                                                "Hotel Booking"}
                                                                        </h3>

                                                                        <p className="mt-1 break-words text-xs leading-5 text-[#71808A] sm:text-sm">
                                                                            {b.room_name ||
                                                                                "Room"}
                                                                            <span className="mx-2 text-[#F7B928]">
                                                                                •
                                                                            </span>
                                                                            Room{" "}
                                                                            {b.room_number ||
                                                                                "—"}
                                                                        </p>

                                                                        <p className="mt-2 text-[10px] font-bold text-[#71808A]">
                                                                            Booking
                                                                            ID: #{b.id}
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                <StatusBadge
                                                                    status={b.status}
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Booking details */}

                                                        <div className="p-5 sm:p-6">
                                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                                                <InfoCard
                                                                    icon={<FaCalendarAlt />}
                                                                    label="Check-in"
                                                                >
                                                                    {formatDate(
                                                                        b.check_in
                                                                    )}
                                                                </InfoCard>

                                                                <InfoCard
                                                                    icon={<FaCalendarAlt />}
                                                                    label="Check-out"
                                                                >
                                                                    {formatDate(
                                                                        b.check_out
                                                                    )}
                                                                </InfoCard>

                                                                <InfoCard
                                                                    icon={<FaUser />}
                                                                    label="Guests"
                                                                >
                                                                    {b.guests ?? "—"}{" "}
                                                                    {Number(b.guests) === 1
                                                                        ? "Guest"
                                                                        : "Guests"}
                                                                </InfoCard>

                                                                <InfoCard
                                                                    icon={
                                                                        <span className="text-[9px] font-extrabold">
                                                                            AED
                                                                        </span>
                                                                    }
                                                                    label="Amount Paid"
                                                                    highlight
                                                                >
                                                                    AED{" "}
                                                                    {Number(
                                                                        b.total_amount || 0
                                                                    ).toLocaleString(
                                                                        "en-AE",
                                                                        {
                                                                            minimumFractionDigits: 2,
                                                                            maximumFractionDigits: 2,
                                                                        }
                                                                    )}
                                                                </InfoCard>
                                                            </div>

                                                            {/* Booking actions */}

                                                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                                                {b.status ===
                                                                    "confirmed" &&
                                                                    canCancel(b) && (
                                                                        <>
                                                                            <button
                                                                                onClick={() =>
                                                                                    cancelBooking(
                                                                                        b.id
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    cancelLoading ===
                                                                                    b.id
                                                                                }
                                                                                className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-xs font-bold transition duration-300 ${cancelLoading ===
                                                                                    b.id
                                                                                    ? "cursor-not-allowed bg-[#FF5A1F]/60 text-white"
                                                                                    : "border border-[#FF5A1F]/20 bg-[#FFF0EC] text-[#C73D23] hover:bg-[#FFE1D7]"
                                                                                    }`}
                                                                            >
                                                                                {cancelLoading ===
                                                                                    b.id ? (
                                                                                    <>
                                                                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                                                                        Cancelling...
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FaTimesCircle />
                                                                                        Cancel Booking
                                                                                    </>
                                                                                )}
                                                                            </button>

                                                                            {(() => {
                                                                                const time =
                                                                                    getCancelCountdown(
                                                                                        b
                                                                                    );

                                                                                if (!time)
                                                                                    return null;

                                                                                return (
                                                                                    <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-[#F7B928]/40 bg-[#FFF9E9] px-4 py-2.5 text-[10px] font-bold text-[#71808A]">
                                                                                        <FaClock className="text-[#0B6E8E]" />

                                                                                        <span>
                                                                                            Free cancellation:
                                                                                        </span>

                                                                                        <span className="font-extrabold tabular-nums text-[#002B45]">
                                                                                            {String(
                                                                                                time.hours
                                                                                            ).padStart(
                                                                                                2,
                                                                                                "0"
                                                                                            )}
                                                                                            :
                                                                                            {String(
                                                                                                time.minutes
                                                                                            ).padStart(
                                                                                                2,
                                                                                                "0"
                                                                                            )}
                                                                                            :
                                                                                            {String(
                                                                                                time.seconds
                                                                                            ).padStart(
                                                                                                2,
                                                                                                "0"
                                                                                            )}
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </>
                                                                    )}

                                                                {b.status ===
                                                                    "confirmed" &&
                                                                    isPastCheckout(
                                                                        b.check_out
                                                                    ) &&
                                                                    !b.review && (
                                                                        <button
                                                                            onClick={() =>
                                                                                openReviewModal(
                                                                                    b
                                                                                )
                                                                            }
                                                                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F7B928] px-5 py-3 text-xs font-extrabold text-[#002B45] transition duration-300 hover:-translate-y-0.5 hover:bg-[#F2AF0B] hover:shadow-md"
                                                                        >
                                                                            <FaStar />
                                                                            Leave Review
                                                                        </button>
                                                                    )}
                                                            </div>

                                                            {b.status ===
                                                                "confirmed" &&
                                                                !isPastCheckout(
                                                                    b.check_out
                                                                ) && (
                                                                    <div className="mt-4 flex items-start gap-2 text-xs leading-5 text-[#71808A]">
                                                                        <FaClock className="mt-0.5 shrink-0 text-[#0B6E8E]" />

                                                                        <span>
                                                                            Reviews
                                                                            can be
                                                                            submitted
                                                                            after
                                                                            checkout.
                                                                        </span>
                                                                    </div>
                                                                )}

                                                            {/* Existing review */}

                                                            {b.review && (
                                                                <div className="mt-6 overflow-hidden rounded-2xl border border-[#DCE7ED] bg-[#F4F8FC]">
                                                                    <div className="flex flex-col gap-3 border-b border-[#DCE7ED] p-4 sm:flex-row sm:items-center sm:justify-between">
                                                                        <div className="flex items-center gap-3">
                                                                            <IconCircle
                                                                                color="yellow"
                                                                                size="h-10 w-10"
                                                                            >
                                                                                <FaStar />
                                                                            </IconCircle>

                                                                            <div>
                                                                                <h4 className="font-extrabold text-[#002B45]">
                                                                                    Your Review
                                                                                </h4>

                                                                                <p className="mt-1 text-[10px] text-[#71808A]">
                                                                                    Your
                                                                                    hotel
                                                                                    experience
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#FFF9E9] px-3 py-2 text-xs font-extrabold text-[#8A6410]">
                                                                            <FaStar className="text-[#F7B928]" />

                                                                            {b.review.rating}
                                                                            /5
                                                                        </span>
                                                                    </div>

                                                                    <div className="p-5">
                                                                        <div className="flex gap-1">
                                                                            {[1, 2, 3, 4, 5].map(
                                                                                (star) => (
                                                                                    <FaStar
                                                                                        key={star}
                                                                                        className={
                                                                                            star <=
                                                                                                Number(
                                                                                                    b.review.rating
                                                                                                )
                                                                                                ? "text-[#F7B928]"
                                                                                                : "text-[#DCE7ED]"
                                                                                        }
                                                                                    />
                                                                                )
                                                                            )}
                                                                        </div>

                                                                        <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-7 text-[#71808A]">
                                                                            {b.review.comment ||
                                                                                "No written comment."}
                                                                        </p>

                                                                        <div className="mt-5 flex flex-wrap gap-3">
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleEditReview(
                                                                                        b
                                                                                    )
                                                                                }
                                                                                className="inline-flex items-center gap-2 rounded-full bg-[#0B6E8E] px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#002B45]"
                                                                            >
                                                                                <FaEdit />
                                                                                Edit Review
                                                                            </button>

                                                                            <button
                                                                                onClick={() =>
                                                                                    handleDeleteReview(
                                                                                        b.review.id
                                                                                    )
                                                                                }
                                                                                className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/20 bg-[#FFF0EC] px-4 py-2.5 text-xs font-bold text-[#C73D23] transition hover:bg-[#FFE1D7]"
                                                                            >
                                                                                <FaTrash />
                                                                                Delete Review
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </article>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            </section>

            {/* REVIEW MODAL */}

            {showReviewModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-[#002B45]/80 px-4 py-6 backdrop-blur-sm"
                    onClick={closeReviewModal}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="review-modal-title"
                        className="relative my-auto w-full max-w-md overflow-hidden rounded-[26px] border border-[#DCE7ED] bg-white shadow-[0_30px_90px_rgba(0,0,0,0.25)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex h-1.5">
                            <div className="w-1/2 bg-[#FF5A1F]" />
                            <div className="w-1/2 bg-[#F7B928]" />
                        </div>

                        <div className="p-6 sm:p-8">
                            {/* Modal header */}

                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <IconCircle
                                        color="yellow"
                                        size="h-12 w-12"
                                    >
                                        <FaStar className="text-lg" />
                                    </IconCircle>

                                    <div className="mt-5">
                                        <SectionEyebrow>
                                            Guest Experience
                                        </SectionEyebrow>
                                    </div>

                                    <h2
                                        id="review-modal-title"
                                        className="mt-3 text-2xl font-extrabold text-[#002B45] sm:text-3xl"
                                    >
                                        {selectedBooking?.review
                                            ? "Edit Review"
                                            : "Share Your Review"}
                                    </h2>

                                    <p className="mt-2 break-words text-xs leading-5 text-[#71808A]">
                                        {selectedBooking?.hotel_name}
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={closeReviewModal}
                                    disabled={loading}
                                    aria-label="Close review modal"
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#DCE7ED] bg-[#F4F8FC] text-[#71808A] transition hover:border-[#FF5A1F] hover:text-[#FF5A1F] disabled:opacity-50"
                                >
                                    <FaTimesCircle />
                                </button>
                            </div>

                            {/* Rating */}

                            <div className="mt-7">
                                <p className="mb-3 text-xs font-extrabold text-[#002B45]">
                                    How was your stay?
                                </p>

                                <div className="flex flex-wrap items-center gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() =>
                                                setRating(star)
                                            }
                                            disabled={loading}
                                            aria-label={`Rate ${star} out of 5 stars`}
                                            aria-pressed={
                                                rating === star
                                            }
                                            className={`flex h-11 w-11 items-center justify-center rounded-xl transition duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed ${star <= rating
                                                ? "bg-[#F7B928] text-[#002B45] shadow-sm"
                                                : "bg-[#F4F8FC] text-[#C6D4DC] hover:bg-[#FFF9E9] hover:text-[#F7B928]"
                                                }`}
                                        >
                                            <FaStar />
                                        </button>
                                    ))}
                                </div>

                                <p className="mt-3 text-xs font-semibold text-[#71808A]">
                                    {rating === 0
                                        ? "Select a rating from 1 to 5 stars"
                                        : [
                                            "",
                                            "Poor",
                                            "Fair",
                                            "Good",
                                            "Very good",
                                            "Excellent",
                                        ][rating]}
                                </p>
                            </div>

                            {/* Comment */}

                            <div className="mt-6">
                                <label
                                    htmlFor="review-comment"
                                    className="mb-2 block text-xs font-extrabold text-[#002B45]"
                                >
                                    Your Experience
                                </label>

                                <textarea
                                    id="review-comment"
                                    value={comment}
                                    onChange={(e) =>
                                        setComment(e.target.value)
                                    }
                                    placeholder="Tell us about your stay..."
                                    disabled={loading}
                                    className="w-full resize-y rounded-2xl border border-[#DCE7ED] bg-[#F4F8FC] p-4 text-sm leading-6 text-[#002B45] outline-none transition placeholder:text-[#71808A] focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/10 disabled:opacity-60"
                                    rows={4}
                                />
                            </div>

                            {/* Submit */}

                            <button
                                type="button"
                                onClick={submitReview}
                                disabled={loading || rating === 0}
                                className={`${primaryButton} mt-6 w-full`}
                            >
                                {loading
                                    ? "Submitting..."
                                    : selectedBooking?.review
                                        ? "Update Review"
                                        : "Submit Review"}

                                {!loading && (
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F7B928] text-[#002B45] transition group-hover:translate-x-1">
                                        <FaArrowRight className="text-xs" />
                                    </span>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={closeReviewModal}
                                disabled={loading}
                                className="mt-3 w-full rounded-full border border-[#DCE7ED] bg-white px-5 py-3 text-xs font-bold text-[#71808A] transition hover:border-[#0B6E8E] hover:text-[#002B45] disabled:opacity-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
